/** @jsxImportSource @emotion/react */
import Toast from 'components/atoms/Toast'
import {
  isNotMailedValid,
  NotMailedReasonFields,
} from 'components/lab/billingPatientStatements/NotMailedReasonFields'
import {
  apiErrorMessage,
  formatDateTime,
  formatMoney,
  formatPatientName,
} from 'components/lab/billingPatientStatements/statementFormat'
import React from 'react'
import { Modal } from 'react-bootstrap'
import {
  useConfirmStatementPrintRunMutation,
  useGetStatementPrintRunQuery,
} from 'store/services/paperStatements'
import {
  billingStatementCycleStepLabel,
  StatementNotMailedDto,
  StatementNotMailedReason,
  StatementPrintRunItemDto,
  StatementPrintRunItemStatus,
} from 'store/types'

type Props = {
  // The run to confirm; the modal is shown while it is set.
  runId: string | null
  onHide: () => void
}

// Every statement starts checked; unchecking one asks why it did not go out.
export const ConfirmPrintRunModal: React.FC<Props> = ({ runId, onHide }) => {
  const [notMailed, setNotMailed] = React.useState<Map<string, StatementNotMailedDto>>(new Map())
  const { data: run, isFetching } = useGetStatementPrintRunQuery(runId || '', { skip: !runId })
  const [confirmRun, { isLoading }] = useConfirmStatementPrintRunMutation()

  React.useEffect(() => {
    setNotMailed(new Map())
  }, [runId])

  const items = (run?.items || []).filter(
    (item: StatementPrintRunItemDto) => item.status === StatementPrintRunItemStatus.PRINTED,
  )
  const isValid = [...notMailed.values()].every(isNotMailedValid)

  const toggle = (itemId: string) => {
    setNotMailed((prev) => {
      const next = new Map(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.set(itemId, { reason: StatementNotMailedReason.NEEDS_REPRINT, note: '' })
      }
      return next
    })
  }

  const setReason = (itemId: string, value: StatementNotMailedDto) => {
    setNotMailed((prev) => new Map(prev).set(itemId, value))
  }

  const handleConfirm = async () => {
    if (!runId) return

    try {
      await confirmRun({
        runId,
        notMailed: [...notMailed.entries()].map(([itemId, value]) => ({
          itemId,
          reason: value.reason,
          note: value.note?.trim() || null,
        })),
      }).unwrap()
      Toast({
        type: 'success',
        label: `${items.length - notMailed.size} statement(s) marked as mailed`,
      })
      onHide()
    } catch (error) {
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to confirm the print run') })
    }
  }

  return (
    <Modal show={!!runId} onHide={onHide} centered size="xl">
      <Modal.Header>
        <Modal.Title>Confirm mailed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="textGray">
          Printed {formatDateTime(run?.printedAt)}{run?.printedByName ? ` by ${run.printedByName}` : ''}.
          Uncheck any statement that didn&apos;t go out and say why.
        </p>
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th style={{ width: 40 }} />
                <th>Patient</th>
                <th>Balance</th>
                <th>Step</th>
                <th style={{ minWidth: 320 }}>Not mailed because</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && (
                <tr>
                  <td colSpan={5} className="text-center">Loading...</td>
                </tr>
              )}
              {!isFetching && !items.length && (
                <tr>
                  <td colSpan={5} className="text-center">Nothing left to confirm in this run</td>
                </tr>
              )}
              {!isFetching && items.map((item: StatementPrintRunItemDto) => {
                const reason = notMailed.get(item.id)

                return (
                  <tr key={item.id}>
                    <td>
                      <input type="checkbox" checked={!reason} onChange={() => toggle(item.id)} />
                    </td>
                    <td>{formatPatientName(item.patientLastName, item.patientFirstName)}</td>
                    <td>{formatMoney(item.balance)}</td>
                    <td>{billingStatementCycleStepLabel(item.stepKey)}</td>
                    <td>
                      {reason
                        ? <NotMailedReasonFields value={reason} onChange={(value) => setReason(item.id, value)} />
                        : <span className="textGray">Mailed</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-outline-secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={isLoading || isFetching || !items.length || !isValid}>
          {isLoading ? 'Confirming…' : `Confirm ${items.length - notMailed.size} mailed`}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
